import { appConfig } from "app-config";
import i18n from "i18n";

// Track namespaces currently being loaded to prevent concurrent loads
const loadingNamespaces = new Set<string>();
// Track namespaces that have been successfully loaded
const loadedNamespaces = new Set<string>();
// Store current seller ID for non-React contexts (updated from PrivateRoute)
let currentSellerId: number | undefined;

// Mapping of seller namespaces based on environment and sellerID
const SELLER_NAMESPACES: { [key: string]: { [key: number]: string } } = {
  DEV: {
    1: "pv",
    1056: "sgc",
    1020: "aja",
  },
  STAG: {
    1: "pv",
    488: "sgc",
    49: "aja",
  },
  PROD: {
    1: "pv",
    2724: "sgc",
    1340: "aja",
  },
};

/**
 * Set current seller ID for use in translation utilities
 * Should be called from PrivateRoute when user context is available
 */
const setCurrentSellerId = (sellerId: number | undefined) => {
  currentSellerId = sellerId;
};

/**
 * Get current seller ID
 * Used by translation utilities to resolve seller-specific translations
 */
const getCurrentSellerId = (): number | undefined => {
  return currentSellerId;
};

/**
 * Utility function to load seller-specific translation namespace
 * Should be called once during app initialization (e.g., in PrivateRoute)
 */
const loadSellerNamespace = async (sellerId: number | undefined) => {
  if (!sellerId) return;

  const env = appConfig.env === "TEST-1" ? "DEV" : appConfig.env;
  const namespacesForEnv = SELLER_NAMESPACES[env];
  const sellerNamespace = namespacesForEnv[sellerId];

  if (!sellerNamespace) return;

  const currentLang = i18n.language;
  const namespaceKey = `${currentLang}:${sellerNamespace}`;

  // Early return if already loaded or currently loading
  if (loadedNamespaces.has(namespaceKey)) {
    console.log(`Namespace ${sellerNamespace} already loaded for ${currentLang}`);
    return;
  }

  if (loadingNamespaces.has(namespaceKey)) {
    console.log(`Namespace ${sellerNamespace} is already loading for ${currentLang}`);
    return;
  }

  // Mark as loading
  loadingNamespaces.add(namespaceKey);

  try {
    const hasResouces = i18n.hasResourceBundle(currentLang, sellerNamespace);
    if (!hasResouces) {
      // Dynamically import the namespace resources
      try {
        const translations = await import(`../locales/${currentLang}/${sellerNamespace}.json`);

        // Add resource bundle dynamically
        // Parameters: (language, namespace, resources, deep, overwrite)
        i18n.addResourceBundle(
          currentLang,
          sellerNamespace,
          translations,
          true, // deep: merge with existing
          true // overwrite: replace existing keys
        );

        console.log(`✓ Loaded ${sellerNamespace} translations for ${currentLang}`);
      } catch (error) {
        console.warn(`Failed to load ${sellerNamespace} namespace:`, error);
        // Continue with fallback translations
        return;
      }
    }

    await i18n.loadNamespaces(sellerNamespace);

    // To trigger re-render with new namespace
    await i18n.changeLanguage(i18n.language);

    // Mark as successfully loaded
    loadedNamespaces.add(namespaceKey);
  } catch (error) {
    console.error(`Error loading namespace ${sellerNamespace}:`, error);
  } finally {
    // Remove from loading set
    loadingNamespaces.delete(namespaceKey);
  }
};

/**
 * Get the seller namespace for a given seller ID
 */
const getSellerNamespace = (sellerId: number | undefined): string | null => {
  if (!sellerId) return null;

  const env = appConfig.env === "TEST-1" ? "DEV" : appConfig.env;
  const namespacesForEnv = SELLER_NAMESPACES[env];

  return namespacesForEnv[sellerId] || null;
};

export default {
  getSellerNamespace,
  loadSellerNamespace,
  setCurrentSellerId,
  getCurrentSellerId,
};
